// web/src/components/OnboardingCard.tsx
// Guided "first kid / first match" panel surfaced on the dashboard for users
// who haven't completed onboarding yet. Three states:
//   1. No kid → primary CTA "Add your first child"
//   2. Has kid, not yet dismissed → primary CTA "Record your first match",
//      secondary "Don't show me this again" (PATCHes parent.onboardedAt)
//   3. Dismissed → component returns null (caller renders nothing).
//
// The match-recording flow itself lives in the native app; on web we send
// users to the kid's detail page where they can use the "Record a match"
// button. (Match recording on web is supported but designed as a fallback;
// the on-pitch experience is the native app.)

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import type { Parent, Kid } from '../types';
import { useParentApi } from '../api/parent';

interface Props {
  parent: Parent;
  kids: Kid[];
  onDismissed: () => void;
}

export function OnboardingCard({ parent, kids, onDismissed }: Props) {
  const api = useParentApi();
  const [dismissing, setDismissing] = useState(false);

  // Already onboarded → render nothing.
  if (parent.onboardedAt) return null;

  const hasKid = kids.length > 0;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.updateMe({ onboardedAt: true });
      onDismissed();
    } catch {
      // Silently re-allow another attempt — onboarding is best-effort.
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div style={{
      padding: 20,
      borderRadius: 16,
      background: 'linear-gradient(135deg, var(--gold) 0%, #d49a5a 100%)',
      color: '#2a1d0c',
      marginBottom: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <h2 style={{ fontSize: 18, margin: 0, lineHeight: 1.3 }}>
          {hasKid ? "Let's record a match" : "Welcome — let's add your child"}
        </h2>
        {hasKid ? (
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            aria-label="Dismiss onboarding"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#2a1d0c',
              opacity: dismissing ? 0.5 : 0.7,
              cursor: 'pointer',
              fontSize: 18,
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        ) : null}
      </div>

      <p style={{ margin: '8px 0 16px', fontSize: 14, opacity: 0.85 }}>
        {hasKid
          ? `Open ${kids[0]?.name ?? "your child"}'s profile and tap "Record a match". Or use the native app on match day — the live recorder is on your phone.`
          : "Start by adding the child whose journal you're going to keep. Just a name and date of birth is enough."}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {hasKid && kids[0] ? (
          <Link to={`/kids/${kids[0].kidId}`} style={{ textDecoration: 'none' }}>
            <Button label="Open profile" />
          </Link>
        ) : (
          <Link to="/kids/new" style={{ textDecoration: 'none' }}>
            <Button label="Add your first child" />
          </Link>
        )}
        {hasKid ? (
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            style={{
              background: 'transparent',
              border: '1px solid rgba(42,29,12,0.3)',
              color: '#2a1d0c',
              padding: '10px 16px',
              borderRadius: 999,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 14,
              opacity: dismissing ? 0.5 : 1,
            }}
          >
            {dismissing ? 'Saving…' : "Don't show this again"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
